import { Injectable } from '@angular/core';
import { Call } from '@twilio/voice-sdk';
import { Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import {
  CallQuality,
  CallSignalLevel,
  INITIAL_CALL_QUALITY,
  LEVEL_CHANGE_SAMPLES,
  mosToSignalLevel,
  MOS_WINDOW_SAMPLES,
  NETWORK_WARNINGS,
} from 'src/app/models/call-quality.model';

/** The parts of a Call this service listens to, so tests can hand in a plain emitter. */
export type CallQualitySource = Pick<Call, 'on' | 'removeListener'>;

/**
 * Turns the Voice SDK's per-call statistics into what the care manager needs to see: how good the
 * connection is, whether it is reconnecting, and whether the network is degrading the audio.
 *
 * Everything comes from the browser leg, in real time and at no cost: the SDK samples the WebRTC
 * stats every second (`sample`, with an estimated MOS) and raises `warning` when a threshold is
 * crossed. Twilio's server-side Voice Insights would add the carrier leg, but only after the call
 * and only with Advanced Features, which the account does not have.
 */
@Injectable({ providedIn: 'root' })
export class CallQualityService {
  watch(call: CallQualitySource): Observable<CallQuality> {
    return new Observable<CallQuality>((subscriber) => {
      const mosWindow: number[] = [];
      let level: CallSignalLevel = INITIAL_CALL_QUALITY.level;
      let candidate: CallSignalLevel | null = null;
      let candidateSamples = 0;
      let reconnecting = false;
      const warnings = new Set<string>();

      const emit = () => subscriber.next({ level, reconnecting, networkWarnings: [...warnings].sort() });

      const onSample = (sample: { mos?: number | null }) => {
        if (typeof sample?.mos !== 'number') {
          return;
        }
        mosWindow.push(sample.mos);
        if (mosWindow.length > MOS_WINDOW_SAMPLES) {
          mosWindow.shift();
        }
        const average = mosWindow.reduce((sum, mos) => sum + mos, 0) / mosWindow.length;
        const next = mosToSignalLevel(average);

        if (next === level) {
          candidate = null;
          candidateSamples = 0;
          return;
        }
        candidateSamples = next === candidate ? candidateSamples + 1 : 1;
        candidate = next;
        if (candidateSamples >= LEVEL_CHANGE_SAMPLES) {
          level = next;
          candidate = null;
          candidateSamples = 0;
          emit();
        }
      };
      const onWarning = (name: string) => {
        if (NETWORK_WARNINGS.includes(name) && !warnings.has(name)) {
          warnings.add(name);
          emit();
        }
      };
      const onWarningCleared = (name: string) => {
        if (warnings.delete(name)) {
          emit();
        }
      };
      const onReconnecting = () => {
        reconnecting = true;
        emit();
      };
      const onReconnected = () => {
        reconnecting = false;
        emit();
      };

      call.on('sample', onSample);
      call.on('warning', onWarning);
      call.on('warning-cleared', onWarningCleared);
      call.on('reconnecting', onReconnecting);
      call.on('reconnected', onReconnected);
      emit();

      return () => {
        call.removeListener('sample', onSample);
        call.removeListener('warning', onWarning);
        call.removeListener('warning-cleared', onWarningCleared);
        call.removeListener('reconnecting', onReconnecting);
        call.removeListener('reconnected', onReconnected);
      };
    }).pipe(distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)));
  }
}
